import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import '@fontsource/rubik';

const SPOTLIGHT_PADDING = 8;
const TOOLTIP_WIDTH = 320;
const VIEWPORT_MARGIN = 16;
const GAP = 18;
const ARROW_SIZE = 11;
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const TRANSITION_MS = 320;

function measureTarget(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - SPOTLIGHT_PADDING,
    left: r.left - SPOTLIGHT_PADDING,
    width: r.width + SPOTLIGHT_PADDING * 2,
    height: r.height + SPOTLIGHT_PADDING * 2,
    bottom: r.bottom + SPOTLIGHT_PADDING,
    right: r.right + SPOTLIGHT_PADDING,
    centerX: r.left + r.width / 2,
    centerY: r.top + r.height / 2,
  };
}

// Picks the side with room for the tooltip (falling back to whichever side
// has the most space) so the card never overlaps the spotlighted element,
// then clamps it fully inside the viewport so it never gets cut off.
function resolveTooltipPosition(preferred, rect, tw, th) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spaceBelow = vh - rect.bottom;
  const spaceAbove = rect.top;
  const spaceRight = vw - rect.right;
  const spaceLeft = rect.left;
  const space = { bottom: spaceBelow, top: spaceAbove, right: spaceRight, left: spaceLeft };

  const required = GAP + VIEWPORT_MARGIN;
  const fits = {
    bottom: spaceBelow >= th + required,
    top: spaceAbove >= th + required,
    right: spaceRight >= tw + required,
    left: spaceLeft >= tw + required,
  };

  let placement = preferred;
  if (!fits[placement]) {
    const opposite = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }[placement];
    if (fits[opposite]) {
      placement = opposite;
    } else {
      const ranked = Object.keys(space).sort((a, b) => space[b] - space[a]);
      placement = ranked.find((p) => fits[p]) || ranked[0];
    }
  }

  let top;
  let left;
  if (placement === 'top') {
    top = rect.top - GAP - th;
    left = rect.centerX - tw / 2;
  } else if (placement === 'bottom') {
    top = rect.bottom + GAP;
    left = rect.centerX - tw / 2;
  } else if (placement === 'left') {
    left = rect.left - GAP - tw;
    top = rect.centerY - th / 2;
  } else {
    left = rect.right + GAP;
    top = rect.centerY - th / 2;
  }

  top = Math.min(Math.max(top, VIEWPORT_MARGIN), vh - th - VIEWPORT_MARGIN);
  left = Math.min(Math.max(left, VIEWPORT_MARGIN), vw - tw - VIEWPORT_MARGIN);

  return { top, left, placement };
}

// Arrow is a 45deg-rotated square sharing the card's border, positioned to
// point from the tooltip edge back toward the spotlighted element.
function getArrowStyle(placement, rect, top, left, tw, th) {
  const base = {
    position: 'absolute',
    width: ARROW_SIZE,
    height: ARROW_SIZE,
    backgroundColor: '#ffffff',
    transform: 'rotate(45deg)',
    zIndex: 1,
  };
  const clampRange = (v, max) => Math.min(Math.max(v, 16), Math.max(max - 16 - ARROW_SIZE, 16));

  if (placement === 'top') {
    return {
      ...base,
      bottom: -ARROW_SIZE / 2,
      left: clampRange(rect.centerX - left - ARROW_SIZE / 2, tw),
      borderRight: '1px solid #E9EBE9',
      borderBottom: '1px solid #E9EBE9',
    };
  }
  if (placement === 'right') {
    return {
      ...base,
      left: -ARROW_SIZE / 2,
      top: clampRange(rect.centerY - top - ARROW_SIZE / 2, th),
      borderLeft: '1px solid #E9EBE9',
      borderBottom: '1px solid #E9EBE9',
    };
  }
  if (placement === 'left') {
    return {
      ...base,
      right: -ARROW_SIZE / 2,
      top: clampRange(rect.centerY - top - ARROW_SIZE / 2, th),
      borderRight: '1px solid #E9EBE9',
      borderTop: '1px solid #E9EBE9',
    };
  }
  // bottom (default)
  return {
    ...base,
    top: -ARROW_SIZE / 2,
    left: clampRange(rect.centerX - left - ARROW_SIZE / 2, tw),
    borderLeft: '1px solid #E9EBE9',
    borderTop: '1px solid #E9EBE9',
  };
}

/**
 * Reusable spotlight product tour. Dims the page, cuts a highlighted "hole"
 * around the target element for each step, and shows a tooltip card (with a
 * pointer arrow) that has title/description + Back/Next controls. The card
 * always resolves to whichever side has room, so it never overlaps the
 * highlighted element and never gets clipped by the viewport edge.
 * Steps: [{ selector, title, content, placement }]
 */
export default function ProductTour({ steps, open, onClose, storageKey }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [settled, setSettled] = useState(false);
  const [tooltipHeight, setTooltipHeight] = useState(160);
  const tooltipRef = useRef(null);

  const step = steps?.[stepIndex];

  const measure = useCallback(() => {
    if (!step) return;
    setRect(measureTarget(step.selector));
  }, [step]);

  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  // On every step change: fade the card out, let the page scroll the target
  // into view, then re-measure and fade back in at the new spot.
  useEffect(() => {
    if (!open || !step) return;
    setSettled(false);
    const el = document.querySelector(step.selector);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => {
      measure();
      setSettled(true);
    }, TRANSITION_MS);
    return () => clearTimeout(t);
  }, [open, step, measure]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, measure]);

  // Keep the measured card height in sync with its actual (variable-length) content.
  useLayoutEffect(() => {
    if (!tooltipRef.current) return;
    const h = tooltipRef.current.getBoundingClientRect().height;
    if (h && Math.abs(h - tooltipHeight) > 1) setTooltipHeight(h);
  }, [step, rect, tooltipHeight]);

  // Lock page scrolling for the duration of the tour, restoring it on close/unmount.
  useEffect(() => {
    if (!open) return;
    const scrollEl = document.getElementById('app-scroll-container');
    const prevBodyOverflow = document.body.style.overflow;
    const prevScrollElOverflow = scrollEl?.style.overflow;
    document.body.style.overflow = 'hidden';
    if (scrollEl) scrollEl.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      if (scrollEl) scrollEl.style.overflow = prevScrollElOverflow;
    };
  }, [open]);

  const handleClose = useCallback(() => {
    if (storageKey) window.localStorage.setItem(storageKey, 'true');
    onClose?.();
  }, [onClose, storageKey]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  if (!open || !step) return null;

  const isLastStep = stepIndex === steps.length - 1;

  const goNext = () => (isLastStep ? handleClose() : setStepIndex((i) => i + 1));
  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));

  const preferredPlacement = step.placement || 'bottom';

  let top = window.innerHeight * 0.4;
  let left = window.innerWidth / 2 - TOOLTIP_WIDTH / 2;
  let resolvedPlacement = preferredPlacement;
  if (rect) {
    const resolved = resolveTooltipPosition(preferredPlacement, rect, TOOLTIP_WIDTH, tooltipHeight);
    top = resolved.top;
    left = resolved.left;
    resolvedPlacement = resolved.placement;
  }

  const arrowStyle = rect ? getArrowStyle(resolvedPlacement, rect, top, left, TOOLTIP_WIDTH, tooltipHeight) : null;

  return createPortal(
    <>
      {/* Dimmed backdrop with a spotlight "hole" cut around the target */}
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
        {rect ? (
          <Box
            sx={{
              position: 'fixed',
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              borderRadius: '12px',
              boxShadow: '0 0 0 9999px rgba(8,35,27,0.65)',
              transition: `top ${TRANSITION_MS}ms ${EASE}, left ${TRANSITION_MS}ms ${EASE}, width ${TRANSITION_MS}ms ${EASE}, height ${TRANSITION_MS}ms ${EASE}`,
              pointerEvents: 'none',
            }}
          />
        ) : (
          <Box sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(8,35,27,0.65)' }} />
        )}
      </Box>

      {/* Tooltip card */}
      <Box
        ref={tooltipRef}
        sx={{
          position: 'fixed',
          top,
          left,
          width: TOOLTIP_WIDTH,
          zIndex: 2001,
          backgroundColor: '#ffffff',
          borderRadius: '14px',
          border: '1px solid #E9EBE9',
          borderTop: '3px solid #00D744',
          boxShadow: '0 16px 40px rgba(8,35,27,0.35), 0 4px 12px rgba(8,35,27,0.15)',
          p: '18px',
          fontFamily: 'Rubik',
          opacity: settled ? 1 : 0,
          transition: `opacity ${TRANSITION_MS}ms ${EASE}, top ${TRANSITION_MS}ms ${EASE}, left ${TRANSITION_MS}ms ${EASE}`,
        }}
      >
        {arrowStyle && <Box sx={arrowStyle} />}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Rubik',
              fontSize: '10.5px',
              fontWeight: 700,
              color: '#00A83B',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
          >
            Step {stepIndex + 1} of {steps.length}
          </Typography>
          <IconButton size="small" onClick={handleClose} sx={{ p: 0.25, mt: '-4px', mr: '-6px', color: '#6b7470' }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Typography sx={{ fontFamily: 'Rubik', fontSize: '15px', fontWeight: 700, color: '#08231B', mb: 0.5 }}>
          {step.title}
        </Typography>
        <Typography sx={{ fontFamily: 'Rubik', fontSize: '13px', color: '#6b7470', lineHeight: 1.5, mb: 2 }}>
          {step.content}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box
            component="button"
            onClick={handleClose}
            sx={{
              background: 'none', border: 0, cursor: 'pointer', p: 0,
              fontFamily: 'Rubik', fontSize: '12px', fontWeight: 600, color: '#6b7470',
            }}
          >
            Skip tour
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {stepIndex > 0 && (
              <Box
                component="button"
                onClick={goBack}
                sx={{
                  background: '#fff', border: '1px solid #E9EBE9', borderRadius: '8px',
                  px: '14px', py: '7px', cursor: 'pointer',
                  fontFamily: 'Rubik', fontSize: '13px', fontWeight: 600, color: '#08231B',
                  transition: `border-color ${TRANSITION_MS}ms ${EASE}`,
                  '&:hover': { borderColor: '#a8aea8' },
                }}
              >
                Back
              </Box>
            )}
            <Box
              component="button"
              onClick={goNext}
              sx={{
                background: '#08231B', border: 0, borderRadius: '8px',
                px: '16px', py: '7px', cursor: 'pointer',
                fontFamily: 'Rubik', fontSize: '13px', fontWeight: 600, color: '#ffffff',
                boxShadow: '0 2px 8px rgba(8,35,27,0.3)',
                transition: `background ${TRANSITION_MS}ms ${EASE}, transform 0.15s ${EASE}`,
                '&:hover': { background: '#0d3327', transform: 'translateY(-1px)' },
              }}
            >
              {isLastStep ? 'Finish' : 'Next'}
            </Box>
          </Box>
        </Box>
      </Box>
    </>,
    document.body,
  );
}
